import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Modal des Paramètres de Facturation : renseigne les coordonnées de l'émetteur
 * (société, SIRET, adresse, TVA, IBAN...) qui apparaîtront dans l'en-tête PDF.
 * Props :
 *  - appConfig : configuration courante du tenant (contient les billing_*)
 *  - onClose   : () => void
 *  - onSaved   : (updatedConfig) => void (refresh AppConfigContext)
 */
const BillingSettingsModal = ({ appConfig, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoUrl, setLogoUrl] = useState(appConfig?.logo_url || "");
  const [form, setForm] = useState({
    billing_company_name: appConfig?.billing_company_name || appConfig?.company_name || "",
    billing_legal_form: appConfig?.billing_legal_form || "",
    billing_siret: appConfig?.billing_siret || "",
    billing_vat_number: appConfig?.billing_vat_number || "",
    billing_address: appConfig?.billing_address || "",
    billing_zip: appConfig?.billing_zip || "",
    billing_city: appConfig?.billing_city || "",
    billing_country: appConfig?.billing_country || "France",
    billing_email: appConfig?.billing_email || "",
    billing_phone: appConfig?.billing_phone || "",
    billing_iban: appConfig?.billing_iban || "",
    billing_bic: appConfig?.billing_bic || "",
    billing_footer: appConfig?.billing_footer || "",
  });

  useEffect(() => {
    // Bloquer le scroll de la page
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const handleChange = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Fichier trop lourd (max 5 Mo)"); return; }
    setUploadingLogo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await axios.post(`${API}/setup/upload-logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = up.data.url;
      // Persister via /branding pour que logo_url soit lié à la config du tenant
      await axios.put(`${API}/setup/branding`, {
        company_name: form.billing_company_name || appConfig?.company_name || "Ma Conciergerie",
        primary_color: appConfig?.primary_color || "#DC2626",
        logo_url: url,
      });
      setLogoUrl(url);
      toast.success("Logo mis à jour");
    } catch (err) {
      toast.error(`Erreur upload : ${err.response?.data?.detail || err.message}`);
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!logoUrl) return;
    if (!window.confirm("Retirer le logo de l'en-tête des factures ?")) return;
    try {
      await axios.put(`${API}/setup/branding`, {
        company_name: form.billing_company_name || appConfig?.company_name || "Ma Conciergerie",
        primary_color: appConfig?.primary_color || "#DC2626",
        logo_url: "",
      });
      setLogoUrl("");
      toast.success("Logo retiré");
    } catch (err) {
      toast.error(`Erreur : ${err.response?.data?.detail || err.message}`);
    }
  };

  const inputCls = "w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500";
  const labelCls = "block text-xs font-semibold text-gray-700 mb-1";

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/setup/billing`, form);
      toast.success("Paramètres de facturation enregistrés");
      if (onSaved) onSaved({ ...(res.data.billing || form), logo_url: logoUrl });
      onClose();
    } catch (e) {
      toast.error(`Erreur : ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="billing-settings-modal"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center z-10">
          <div>
            <h3 className="text-lg font-bold text-gray-900">⚙️ Paramètres de facturation</h3>
            <p className="text-xs text-gray-600">Ces informations apparaissent dans l'en-tête des factures PDF.</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            data-testid="billing-settings-close-btn"
          >✕</button>
        </div>

        <div className="p-6 space-y-4">
          {/* Logo */}
          <div>
            <label className={labelCls}>Logo (apparaît sur les factures PDF)</label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center bg-gray-50 overflow-hidden">
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="billing-logo-preview" />
                ) : (
                  <span className="text-3xl text-gray-400">🖼️</span>
                )}
              </div>
              <div className="flex-1 space-y-2">
                <label className="inline-flex items-center px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg cursor-pointer">
                  {uploadingLogo ? "Envoi..." : (logoUrl ? "Remplacer" : "Choisir un fichier")}
                  <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} disabled={uploadingLogo} data-testid="billing-logo-input" />
                </label>
                {logoUrl && (
                  <button
                    type="button"
                    onClick={handleRemoveLogo}
                    className="ml-2 inline-flex items-center px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-medium rounded-lg"
                    data-testid="billing-logo-remove"
                  >Retirer</button>
                )}
                <p className="text-xs text-gray-500">PNG/JPG/WebP · 5 Mo max · Recommandé : ≥ 200 px de large sur fond transparent</p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className={labelCls}>Nom de la société<span className="text-red-500 ml-1">*</span></label>
              <input type="text" value={form.billing_company_name} onChange={handleChange("billing_company_name")}
                     placeholder="Ma Conciergerie" className={inputCls} data-testid="billing-company-name" />
            </div>
            <div>
              <label className={labelCls}>Forme juridique</label>
              <input type="text" value={form.billing_legal_form} onChange={handleChange("billing_legal_form")}
                     placeholder="SAS, SARL, EI…" className={inputCls} data-testid="billing-legal-form" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>SIRET</label>
              <input type="text" value={form.billing_siret} onChange={handleChange("billing_siret")}
                     placeholder="14 chiffres" className={inputCls} data-testid="billing-siret" />
            </div>
            <div>
              <label className={labelCls}>N° TVA intracom.</label>
              <input type="text" value={form.billing_vat_number} onChange={handleChange("billing_vat_number")}
                     placeholder="FR..." className={inputCls} data-testid="billing-vat" />
            </div>
          </div>

          <div>
            <label className={labelCls}>Adresse</label>
            <input type="text" value={form.billing_address} onChange={handleChange("billing_address")}
                   placeholder="1 rue de la Paix" className={inputCls} data-testid="billing-address" />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className={labelCls}>Code postal</label>
              <input type="text" value={form.billing_zip} onChange={handleChange("billing_zip")}
                     placeholder="84000" className={inputCls} data-testid="billing-zip" />
            </div>
            <div>
              <label className={labelCls}>Ville</label>
              <input type="text" value={form.billing_city} onChange={handleChange("billing_city")}
                     placeholder="Avignon" className={inputCls} data-testid="billing-city" />
            </div>
            <div>
              <label className={labelCls}>Pays</label>
              <input type="text" value={form.billing_country} onChange={handleChange("billing_country")}
                     placeholder="France" className={inputCls} data-testid="billing-country" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Email</label>
              <input type="email" value={form.billing_email} onChange={handleChange("billing_email")}
                     placeholder="contact@..." className={inputCls} data-testid="billing-email" />
            </div>
            <div>
              <label className={labelCls}>Téléphone</label>
              <input type="text" value={form.billing_phone} onChange={handleChange("billing_phone")}
                     placeholder="06 12 34 56 78" className={inputCls} data-testid="billing-phone" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>IBAN</label>
              <input type="text" value={form.billing_iban} onChange={handleChange("billing_iban")}
                     placeholder="FR76 ..." className={inputCls} data-testid="billing-iban" />
            </div>
            <div>
              <label className={labelCls}>BIC</label>
              <input type="text" value={form.billing_bic} onChange={handleChange("billing_bic")}
                     placeholder="BNPAFR..." className={inputCls} data-testid="billing-bic" />
            </div>
          </div>

          <div>
            <label className={labelCls}>Mentions légales / Pied de page</label>
            <textarea value={form.billing_footer} onChange={handleChange("billing_footer")} rows={3}
              placeholder="TVA non applicable, art. 293 B du CGI — Paiement à réception..."
              className={inputCls} data-testid="billing-footer" />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t px-6 py-3 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium"
            data-testid="billing-settings-cancel-btn"
          >Annuler</button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg font-medium"
            data-testid="billing-settings-save-btn"
          >{saving ? "Enregistrement..." : "Enregistrer"}</button>
        </div>
      </div>
    </div>
  );
};

export default BillingSettingsModal;
